
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import React, { Suspense, lazy } from "react";
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import { EarlyAdopterProvider } from "@/contexts/EarlyAdopterContext";
import EarlyAdopterModal from "@/components/early-adopter/EarlyAdopterModal";
import { AudienceProvider } from "@/contexts/AudienceContext";

// Core pages - direct imports to avoid dynamic loading issues
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import WhySeniorThrive from "./pages/WhySeniorThrive";


import OlderAdults from "./pages/OlderAdults";
import FamilyMembers from "./pages/FamilyMembers";
import CareTools from "./pages/CareTools";
import ThriveCare from "./pages/ThriveCare";
import Pricing from "./pages/Pricing";
import OccupationalTherapists from "./pages/OccupationalTherapists";
import OTDirectory from "./pages/OTDirectory";
import SeniorCareProfessionalsDirectory from "./pages/SeniorCareProfessionalsDirectory";
import IndependentCaregiversDirectory from "./pages/IndependentCaregiversDirectory";
import ProfessionalDirectory from "./pages/ProfessionalDirectory";
import ProfessionalProfile from "./pages/ProfessionalProfile";
import Support from "./pages/Support";
import SupportCategory from "./pages/SupportCategory";
import SupportArticle from "./pages/SupportArticle";
import SupportAll from "./pages/SupportAll";
import SupportOlderAdults from "./pages/SupportOlderAdults";
import SupportOlderAdultsArticle from "./pages/SupportOlderAdultsArticle";
import Partners from "./pages/Partners";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Contact from "./pages/Contact";
import BookingPage from "./pages/BookingPage";
import Sponsorship from "./pages/Sponsorship";
import OTCampaignLanding from "./pages/OTCampaignLanding";
import CapsSpecialists from "./pages/CapsSpecialists";
import CapsDirectory from "./pages/CapsDirectory";
import ProfessionalNetwork from "./pages/ProfessionalNetwork";
import ProfessionalNetworkSuccess from "./pages/ProfessionalNetworkSuccess";
import { UnifiedProfessionalDirectory } from "./components/UnifiedProfessionalDirectory";
import About from "./pages/About";
import HealthSync from "./pages/HealthSync";
import Features from "./pages/Features";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import ReferralAgreement from "./pages/ReferralAgreement";
import AgingInPlace from "./pages/AgingInPlace";


import Stories from "./pages/Stories";
import FAQ from "./pages/FAQ";
import GetVIP from "./pages/GetVIP";
import FormDebug from "./pages/FormDebug";
import RobotsTxt from "./components/RobotsTxt";
import Sitemap from "./components/Sitemap";
import BlogSitemap from "./components/BlogSitemap";
import IndexNowKey from "./components/IndexNowKey";

// Admin components
import SiteAudit from "./components/admin/SiteAudit";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminFormsEarlyAdopters from "./pages/admin/AdminFormsEarlyAdopters";
import AdminFormsContact from "./pages/admin/AdminFormsContact";
import AdminFormsSponsor from "./pages/admin/AdminFormsSponsor";
import AdminFormsStories from "./pages/admin/AdminFormsStories";

import AdminFormsProfessional from "./pages/admin/AdminFormsProfessional";
import AdminSEO from "./pages/admin/AdminSEO";
import AdminFormTesting from "./pages/admin/AdminFormTesting";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminImportProfessionals from "./pages/admin/AdminImportProfessionals";
import AdminReferralSources from "./pages/admin/AdminReferralSources";
import AdminReferralEvents from "./pages/admin/AdminReferralEvents";
import AdminRateTables from "./pages/admin/AdminRateTables";
import AdminReferralPayouts from "./pages/admin/AdminReferralPayouts";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { ProtectedAdminRoute } from "@/components/admin/ProtectedAdminRoute";
import { ContentDashboard } from "./pages/admin";
import BlogPostEditor from "./pages/admin/BlogPostEditor";
import BlogPostList from "./pages/admin/BlogPostList";
import BlogBulkImport from "./pages/admin/BlogBulkImport";
import SupportDocEditor from "./pages/admin/SupportDocEditor";
import SupportDocList from "./pages/admin/SupportDocList";
import AppDashboard from "./pages/admin/AppDashboard";
import { AnalyticsProvider } from "@/contexts/AnalyticsContext";
import { useReferralTracking } from "@/hooks/useReferralTracking";
import { IntercomProvider } from "@/contexts/IntercomContext";
import AffiliatePortal from "./pages/AffiliatePortal";
import PartnerLogin from "./pages/PartnerLogin";
import CaregiverApplicationPage from "./pages/CaregiverApplicationPage";
import AgencyCaregiverApplicationPage from "./pages/AgencyCaregiverApplicationPage";
import CaregiverApplicationsPage from "./pages/admin/CaregiverApplicationsPage";
import CareRequestsPage from "./pages/admin/CareRequestsPage";
import FindHelpPage from "./pages/FindHelpPage";
import ActivatePartnerEarnings from "./pages/ActivatePartnerEarnings";
import PartnerGettingStarted from "./pages/PartnerGettingStarted";
import ReferralWelcome from "./pages/ReferralWelcome";
import Unsubscribe from "./pages/Unsubscribe";
import CaregiverThriveCircle from "./pages/CaregiverThriveCircle";

import Survey from "./pages/Survey";
import FeedbackThankYou from "./pages/FeedbackThankYou";
import PitchDeck from "./pages/PitchDeck";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1, // Reduced retries to prevent hanging
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false, // Prevent unnecessary refetches
    },
  },
});

const App = () => {
  // Initialize referral tracking on app load
  useReferralTracking();
  
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
            <AnalyticsProvider>
              <IntercomProvider>
                <BrowserRouter>
                <AudienceProvider>
                <EarlyAdopterProvider>
                  <AdminAuthProvider>
                  <Toaster />
                  <Sonner />
                  <ScrollToTop />
                  <Routes>
                  {/* Plain text/XML routes - outside Layout */}
                  <Route path="/robots.txt" element={<RobotsTxt />} />
                  <Route path="/sitemap.xml" element={<Sitemap />} />
                  <Route path="/blog-sitemap.xml" element={<BlogSitemap />} />
                  <Route path="/wpm8hj6T5pk7rs9acryuws52anbpxegh.txt" element={<IndexNowKey />} />
                  
                  {/* Standalone pages - outside Layout */}
                  <Route path="/feedback" element={<Survey />} />
                  <Route path="/feedback-thank-you" element={<FeedbackThankYou />} />
                  
                  {/* Admin routes */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/admin/*" element={
                          <ProtectedAdminRoute>
                            <AdminLayout>
                              <Routes>
                                <Route index element={<AdminDashboard />} />
                                <Route path="app" element={<AppDashboard />} />
                                <Route path="analytics" element={<AdminAnalytics />} />
                                <Route path="forms/contact" element={<AdminFormsContact />} />
                                <Route path="forms/early-adopters" element={<AdminFormsEarlyAdopters />} />
                                <Route path="forms/professionals" element={<AdminFormsProfessional />} />
                                <Route path="forms/caregivers" element={<Navigate to="/admin/forms/professionals" replace />} />
                                <Route path="forms/care-requests" element={<Navigate to="/admin/forms/professionals" replace />} />
                                <Route path="forms/sponsors" element={<AdminFormsSponsor />} />
                                <Route path="forms/stories" element={<AdminFormsStories />} />
                                <Route path="seo" element={<AdminSEO />} />
                                <Route path="form-testing" element={<AdminFormTesting />} />
                                <Route path="import" element={<AdminImportProfessionals />} />
                                <Route path="users" element={<AdminUsers />} />
                                <Route path="settings" element={<AdminSettings />} />
                                <Route path="referrals/sources" element={<AdminReferralSources />} />
                                <Route path="referrals/events" element={<AdminReferralEvents />} />
                                <Route path="referrals/rates" element={<AdminRateTables />} />
                                <Route path="referrals/payouts" element={<AdminReferralPayouts />} />
                                <Route path="content" element={<Navigate to="/admin/content/blog/list" replace />} />
                                <Route path="content/blog/list" element={<BlogPostList />} />
                                <Route path="content/blog/new" element={<BlogPostEditor />} />
                                <Route path="content/blog/edit/:id" element={<BlogPostEditor />} />
                                <Route path="content/blog/import" element={<BlogBulkImport />} />
                                <Route path="content/docs/list" element={<SupportDocList />} />
                                <Route path="content/docs/new" element={<SupportDocEditor />} />
                                <Route path="content/docs/edit/:id" element={<SupportDocEditor />} />
                                <Route path="*" element={<Navigate to="/admin" replace />} />
                              </Routes>
                            </AdminLayout>
                          </ProtectedAdminRoute>
                        } />
                  
                  {/* All other routes wrapped in Layout */}
                  <Route path="/*" element={
                    <Layout>
                      <ErrorBoundary>
                        <Routes>
                          <Route path="/" element={<Home />} />
                          <Route path="/about-seniorthrive" element={<WhySeniorThrive />} />
                          <Route path="/why-seniorthrive" element={<Navigate to="/about-seniorthrive" replace />} />
                          
                          
                          <Route path="/older-adults" element={<OlderAdults />} />
                          <Route path="/older-adults-b" element={<Navigate to="/older-adults" replace />} />
                          <Route path="/thrivefamily" element={<FamilyMembers />} />
                          <Route path="/family-members" element={<Navigate to="/thrivefamily" replace />} />
                          <Route path="/family-connection" element={<Navigate to="/thrivefamily" replace />} />
                          <Route path="/family-caregiver-tools" element={<CareTools />} />
                          <Route path="/care-tools" element={<Navigate to="/family-caregiver-tools" replace />} />
                          <Route path="/caregiver-coordination" element={<ThriveCare />} />
                          <Route path="/thrive-care" element={<Navigate to="/caregiver-coordination" replace />} />
                          <Route path="/caregivers" element={<Navigate to="/family-caregiver-tools" replace />} />
                          <Route path="/pricing" element={<Pricing />} />
                          <Route path="/support" element={<Support />} />
                          <Route path="/support/faq" element={<FAQ />} />
                          <Route path="/support/category/:slug" element={<SupportCategory />} />
                          <Route path="/support/article/:slug" element={<SupportArticle />} />
                          <Route path="/support/all" element={<SupportAll />} />
                          <Route path="/support/older-adults" element={<SupportOlderAdults />} />
                          <Route path="/support/older-adults/:slug" element={<SupportOlderAdultsArticle />} />
                          <Route path="/partners" element={<Partners />} />
                          <Route path="/blog" element={<Blog />} />
                          <Route path="/blog/:slug" element={<BlogPost />} />
                          <Route path="/contact" element={<Contact />} />
                          <Route path="/brianthrives" element={<BookingPage />} />
                          <Route path="/sponsor-older-adults" element={<Sponsorship />} />
                          <Route path="/sponsorship" element={<Navigate to="/sponsor-older-adults" replace />} />
                          <Route path="/occupational-therapists" element={<Navigate to="/professional-network" replace />} />
                          <Route path="/occupational-therapists/directory" element={<OTDirectory />} />
                          <Route path="/caps-specialists/directory" element={<CapsDirectory />} />
                          <Route path="/senior-care-professionals/directory" element={<SeniorCareProfessionalsDirectory />} />
                           <Route path="/independent-caregivers/directory" element={<IndependentCaregiversDirectory />} />
                           <Route path="/caregivers/directory" element={<Navigate to="/professionals/directory?role=caregiver_agency" replace />} />
                           <Route path="/caregivers/apply" element={<Navigate to="/professional-network" replace />} />
                           <Route path="/caregivers/apply/agency" element={<AgencyCaregiverApplicationPage />} />
                           <Route path="/professionals/directory" element={<ProfessionalDirectory />} />
                           <Route path="/find" element={<FindHelpPage />} />
                           <Route path="/caregivers/:slug" element={<Navigate to="/professionals/directory" replace />} />
                             <Route path="/professionals/:slug" element={<ProfessionalProfile />} />
                          <Route path="/professional-network" element={<ProfessionalNetwork />} />
                          <Route path="/professional-network/success" element={<ProfessionalNetworkSuccess />} />
                          
                          <Route path="/ot-campaign" element={<OTCampaignLanding />} />
                           <Route path="/caps-specialists" element={<Navigate to="/professional-network" replace />} />
                           <Route path="/directory" element={<UnifiedProfessionalDirectory professionType="all" />} />
                          
                          <Route path="/stories" element={<Stories />} />
                          
                          {/* VIP Early Access Routes */}
                          <Route path="/get-vip" element={<GetVIP />} />
                          <Route path="/early-access" element={<GetVIP />} />
                          <Route path="/vip" element={<GetVIP />} />
                          
                          {/* Site audit route for testing */}
                          <Route path="/audit" element={<SiteAudit />} />
                          
                          {/* Debug route for form testing */}
                          <Route path="/debug/forms" element={<FormDebug />} />
                          
                          {/* Legacy routes for backward compatibility */}
                          <Route path="/about" element={<About />} />
                          <Route path="/features" element={<Features />} />
                          <Route path="/features/:slug" element={
                            <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                              {React.createElement(lazy(() => import("./pages/FeaturePage")))}
                            </Suspense>
                          } />
                          <Route path="/privacy" element={<Privacy />} />
                          <Route path="/terms" element={<Terms />} />
                          <Route path="/referral-agreement" element={<ReferralAgreement />} />
                          <Route path="/affiliate" element={<AffiliatePortal />} />
                          <Route path="/partner-login" element={<PartnerLogin />} />
                          <Route path="/activate-partner-earnings" element={<ActivatePartnerEarnings />} />
                          <Route path="/partner-getting-started" element={<PartnerGettingStarted />} />
                          <Route path="/welcome" element={<ReferralWelcome />} />
                          <Route path="/pitch-deck" element={<PitchDeck />} />
                          <Route path="/unsubscribe" element={<Unsubscribe />} />
                          <Route path="/caregiver/thrivecircle" element={<CaregiverThriveCircle />} />
                          <Route path="/health-sync" element={<HealthSync />} />
                          <Route path="/aging-in-place" element={<AgingInPlace />} />
                          
                          {/* Catch-all route MUST be last */}
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </ErrorBoundary>
                    </Layout>
                  } />
                  </Routes>
                  <EarlyAdopterModal />
                  </AdminAuthProvider>
                </EarlyAdopterProvider>
                </AudienceProvider>
              </BrowserRouter>
              </IntercomProvider>
            </AnalyticsProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};



export default App;
